import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Paper,
  Collapse,
  Button,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import GlassCard from "../../../components/common/GlassCard";

function getStatusColor(status) {
  switch (status?.toLowerCase()) {
    case "scheduled":
      return "info";
    case "completed":
      return "success";
    case "cancelled":
      return "error";
    case "no-show":
      return "warning";
    default:
      return "default";
  }
}

function AppointmentsCard({ appointments, onAddAppointment, onDeleteAppointment }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeAppointmentId, setActiveAppointmentId] = useState(null);
  const [expandAll, setExpandAll] = useState(false);

  const handleMenuOpen = (event, id) => {
    setMenuAnchor(event.currentTarget);
    setActiveAppointmentId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveAppointmentId(null);
  };

  const handleDelete = (appointmentId) => {
    handleMenuClose();
    if (onDeleteAppointment) {
      onDeleteAppointment(appointmentId);
    }
  };

  const sortedAppointments = appointments
    ? [...appointments].sort((a, b) => {
        const dateA = new Date(a.scheduled_at || 0);
        const dateB = new Date(b.scheduled_at || 0);
        return dateB - dateA; // Most recent first
      })
    : [];

  return (
    <GlassCard
      title="Appointments"
      icon={<CalendarTodayIcon color="success" />}
      onAdd={onAddAppointment}
      addLabel="Add Appointment"
      data-testid="appointments-card"
      data-card-category="appointments"
    >
      {sortedAppointments?.length ? (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            {sortedAppointments.slice(0, 2).map((appointment) => (
              <Paper
                key={appointment.id}
                sx={{
                  p: 1.25,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
                data-testid={`appointment-item-${appointment.id}`}
                data-appointment-type={appointment.appointment_type}
                data-appointment-status={appointment.status}
                data-appointment-date={appointment.scheduled_at}
                data-provider={appointment.provider}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={600}>
                      {appointment.appointment_type || "Appointment"} • {appointment.provider || "TBD"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {appointment.scheduled_at
                        ? new Date(appointment.scheduled_at).toLocaleString()
                        : "No date"}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={0.5} alignItems="center">
                    <Chip
                      size="small"
                      label={appointment.status || "scheduled"}
                      color={getStatusColor(appointment.status)}
                      variant="outlined"
                      data-testid={`appointment-status-chip-${appointment.id}`}
                      data-field-name="status"
                    />
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, appointment.id)}
                        data-testid={`appointment-menu-${appointment.id}`}
                        aria-label="Appointment options menu"
                      >
                        <MoreVertIcon aria-label="Appointment options menu" fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>

          {sortedAppointments?.length > 2 && (
            <>
              <Collapse in={expandAll} timeout="auto" unmountOnExit>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                  {sortedAppointments.slice(2).map((appointment) => (
                    <Paper
                      key={appointment.id}
                      sx={{
                        p: 1.25,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                      data-testid={`appointment-item-${appointment.id}`}
                      data-appointment-type={appointment.appointment_type}
                      data-appointment-status={appointment.status}
                      data-appointment-date={appointment.scheduled_at}
                      data-provider={appointment.provider}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                        <Box flex={1}>
                          <Typography variant="body2" fontWeight={600}>
                            {appointment.appointment_type || "Appointment"} • {appointment.provider || "TBD"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {appointment.scheduled_at
                              ? new Date(appointment.scheduled_at).toLocaleString()
                              : "No date"}
                          </Typography>
                        </Box>
                        <Box display="flex" gap={0.5} alignItems="center">
                          <Chip
                            size="small"
                            label={appointment.status || "scheduled"}
                            color={getStatusColor(appointment.status)}
                            variant="outlined"
                            data-testid={`appointment-status-chip-${appointment.id}`}
                            data-field-name="status"
                          />
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, appointment.id)}
                              data-testid={`appointment-menu-${appointment.id}`}
                              aria-label="Appointment options menu"
                            >
                              <MoreVertIcon aria-label="Appointment options menu" fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Collapse>

              <Button
                size="small"
                onClick={() => setExpandAll(!expandAll)}
                startIcon={expandAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ textTransform: "none", mt: 1 }}
              >
                {expandAll ? "View Less" : `View All (${sortedAppointments.length - 2} more)`}
              </Button>
            </>
          )}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No appointments scheduled
        </Typography>
      )}

      {sortedAppointments?.length > 5 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          +{sortedAppointments.length - 5} more appointments
        </Typography>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => handleDelete(activeAppointmentId)}
          data-testid={`appointment-menu-delete-${activeAppointmentId}`}
          data-action="delete"
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </GlassCard>
  );
}

AppointmentsCard.propTypes = {
  appointments: PropTypes.array,
  onAddAppointment: PropTypes.func.isRequired,
  onDeleteAppointment: PropTypes.func.isRequired,
};

AppointmentsCard.defaultProps = {
  appointments: [],
};

export default AppointmentsCard;
